import {
  formatMoney,
  FourPointStarIcon,
  getDefaultBrandclubImage,
  ResponsiveDialog,
  TABLET_SCREEN_SIZE,
} from '@brandclub/common-ui';

import { useBrandAppConfigWithFallback, useDomainConfig } from '@/api/cache/appConfigQueryOptions';
import { alpha, styled, useTheme } from '@mui/material';

import { ShopAppButton } from '@/components/button/ShopAppButton';
import { create } from 'zustand';

const BenefitsContent = styled('div')(({ theme }) => ({
  overflowY: 'auto',
  flex: 1,
  paddingBottom: 12,
  paddingTop: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  // maxWidth: 20,
  // width: "100%",

  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    gap: '5px',
  },
  '&::-webkit-scrollbar': {
    width: 4,
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.primary.main, 0.2),
    borderRadius: 2,
  },
}));

const BenefitCardRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  padding: '2px 2px',
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    gap: 10,
  },
}));

const BenefitCardTitle = styled('div')(({ theme }) => ({
  fontWeight: 600,
  fontSize: 16,
  color: theme.vars.palette.text.primary,
  marginBottom: 2,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    fontSize: 14,
  },
}));

export const ConnectBenefits = ({ rewardAmount }: { rewardAmount?: number }) => {
  const benefitList = [`Get fast, free shipping`, 'Hassle-free returns', 'Access member benefits'];

  const theme = useTheme();

  return (
    <BenefitsContent>
      {benefitList.map((benefit) => (
        <BenefitCardRow>
          <FourPointStarIcon sx={{ width: 16, height: 16 }} fill={theme.palette.primary.main} />
          <BenefitCardTitle>{benefit}</BenefitCardTitle>
        </BenefitCardRow>
      ))}
    </BenefitsContent>
  );
};

const HeaderContainer = styled('div')(({ theme }) => ({
  marginTop: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexDirection: 'column',
  gap: 12,
  [theme.breakpoints.down('md')]: {
    marginTop: 12,
  },

  '.icons': {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 12,
    gap: 32,
    [theme.breakpoints.down('md')]: {
      gap: 6,
    },

    '.icons-groups': {
      display: 'flex',
      alignItems: 'center',
      gap: 32,
      position: 'relative',

      width: 64,
      height: 64,
      [theme.breakpoints.down('md')]: {
        width: 52,
        height: 52,
      },
      '.layer-1': {
        position: 'absolute',
        zIndex: 3,
        top: 0,
        left: -25,
        [theme.breakpoints.down('md')]: {
          left: -22,
        },
      },
      '.layer-2': {
        position: 'absolute',
        zIndex: 2,
        top: 0,
        left: 25,
        [theme.breakpoints.down('md')]: {
          left: 22,
        },
      },
    },
    '.icon': {
      width: 64,
      backgroundColor: '#ffffff',
      height: 64,
      border: '2px solid #ffffff',
      borderRadius: '999px',
      [theme.breakpoints.down('md')]: {
        width: 52,
        height: 52,
      },
      img: {
        borderRadius: 999,
        width: '100%',
        height: '100%',
        objectFit: 'contain',
      },
    },
  },
  '.header-title': {
    fontSize: 20,
    fontWeight: 700,
    color: theme.vars.palette.text.primary,
    textAlign: 'center',
    maxWidth: 374,
  },
  '.header-description': {
    fontSize: 16,
    color: theme.vars.palette.text.primary,
    textAlign: 'center',
    maxWidth: 330,
  },
}));

const CloseButton = styled(ShopAppButton)(({ theme }) => {
  return {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    maxWidth: 380,
    width: '100%',
    height: 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    cursor: 'pointer',
    fontWeight: 600,
  };
});

const Header = ({ rewardAmount }: { rewardAmount?: number }) => {
  const { data: brandAppConfig } = useBrandAppConfigWithFallback();

  const { data: domainConfig } = useDomainConfig();

  const brandLogo = brandAppConfig?.checkoutPageConfig?.logoPicture;
  const brandName = domainConfig?.brandName;

  const dtcBrandLogo =
    brandLogo || (brandAppConfig?.brandIds?.[0] ? getDefaultBrandclubImage(brandAppConfig.brandIds[0]) : '');

  return (
    <HeaderContainer>
      <div className="icons">
        <div className="icons-groups">
          {dtcBrandLogo ? (
            <div className="icon layer-1">
              <img src={dtcBrandLogo} alt="retailer connect" />
            </div>
          ) : null}
          <div className="icon layer-2">
            <img src="https://media.brandclub.com/brandclub/image_asset/brandclub-b-logo.webp" alt="retailer connect" />
          </div>
        </div>
      </div>
      <div className="header-title">
        Get {rewardAmount ? `${formatMoney(rewardAmount)} in ` : ''}cash rewards when you purchase this product
      </div>

      <div className="header-description">{brandName} uses Brandclub to make your purchase more rewarding.</div>
    </HeaderContainer>
  );
};

const Container = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  lineHeight: '24pxg',
  gap: 12,
  minWidth: 580,
  padding: '30px 20px 54px 20px',
}));

export const RetailerConnectBenefits = ({
  rewardAmount,
  onContinue,
}: {
  rewardAmount?: number;
  retailerId?: number;
  onContinue: () => void;
}) => {
  return (
    <Container>
      <Header rewardAmount={rewardAmount} />
      <ConnectBenefits rewardAmount={rewardAmount}></ConnectBenefits>

      <CloseButton variant="solid" onClick={onContinue}>
        Close
      </CloseButton>
    </Container>
  );
};

interface BuyBoxDialogState {
  open: boolean;
  close: () => void;
  rewardAmount?: number;
}

const useBuyBoxDialogStore = create<BuyBoxDialogState>((set) => ({
  open: false,
  close: () => set({ open: false }),
  rewardAmount: 0,
}));

const PurchaseRewardDialog = () => {
  const state = useBuyBoxDialogStore();

  const { open, close, rewardAmount } = state;
  return (
    <ResponsiveDialog disableScrollLock open={open} onClose={close}>
      <RetailerConnectBenefits onContinue={close} rewardAmount={rewardAmount} />
    </ResponsiveDialog>
  );
};

PurchaseRewardDialog.show = (rewardAmount: number) => {
  useBuyBoxDialogStore.setState({ open: true, rewardAmount });
};

export default PurchaseRewardDialog;
